import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import { useSEO, createOrganizationSchema, createWebPageSchema } from '../../utils/seo';

export default function AboutPage() {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState<'mission' | 'vision' | null>(null);
  const [selectedValue, setSelectedValue] = useState<number | null>(null);
  const [selectedReason, setSelectedReason] = useState<number | null>(null);

  useSEO({
    title: 'Hakkımızda - AXIOM Kalite Kontrol ve İnsan Kaynakları Danışmanlığı',
    description: 'AXIOM olarak kalite kontrol ve insan kaynakları alanında profesyonel danışmanlık hizmetleri sunuyoruz. Misyonumuz, vizyonumuz ve değerlerimiz hakkında bilgi edinin.',
    keywords: 'hakkımızda, axiom, kalite kontrol, insan kaynakları, danışmanlık, misyon, vizyon',
    canonical: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/about`,
    schema: {
      '@graph': [
        createOrganizationSchema(),
        createWebPageSchema(
          'Hakkımızda - AXIOM',
          'AXIOM olarak kalite kontrol ve insan kaynakları alanında profesyonel danışmanlık hizmetleri sunuyoruz.'
        )
      ]
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const values = [
    {
      icon: 'ri-shield-check-line',
      title: 'Güvenilirlik',
      description: 'Müşterilerimize karşı her zaman dürüst ve şeffaf olmak',
      details: {
        subtitle: 'Güven İlişkisi Kurmak',
        content: 'Güvenilirlik, iş ilişkilerimizin temelidir. Her projede şeffaflık, dürüstlük ve tutarlılık ilkeleriyle hareket ederiz.',
        points: [
          'Tüm süreçlerde tam şeffaflık ve açık iletişim',
          'Verilen sözlerin zamanında ve eksiksiz yerine getirilmesi',
          'Hata durumlarında proaktif bilgilendirme ve çözüm üretme',
          'Müşteri verilerinin gizliliği ve güvenliğinin korunması'
        ],
        stats: { label: 'Müşteri Güven Oranı', value: '%98' }
      }
    },
    {
      icon: 'ri-lightbulb-line',
      title: 'İnovasyon',
      description: 'Sürekli gelişim ve yenilikçi çözümler üretmek',
      details: {
        subtitle: 'Geleceği Şekillendirmek',
        content: 'Teknolojik gelişmeleri yakından takip ediyor, sektöre yenilikçi çözümler sunuyoruz. Ar-Ge çalışmalarımızla sürekli ilerliyoruz.',
        points: [
          'Yapay zeka destekli kalite kontrol sistemleri geliştirme',
          'IoT sensörleri ile gerçek zamanlı izleme çözümleri',
          'Dijital ikiz teknolojisi ile simülasyon ve optimizasyon',
          'Blockchain tabanlı tedarik zinciri şeffaflığı'
        ],
        stats: { label: 'Ar-Ge Yatırımı', value: '%15' }
      }
    },
    {
      icon: 'ri-team-line',
      title: 'İşbirliği',
      description: 'Ekip çalışması ve ortaklık kültürünü desteklemek',
      details: {
        subtitle: 'Birlikte Başarmak',
        content: 'Güçlü ekip ruhu ve işbirliği kültürü ile müşterilerimizle uzun vadeli ortaklıklar kuruyoruz. Başarı birlikte gelir.',
        points: [
          'Müşteri ekipleriyle entegre çalışma modeli',
          'Düzenli bilgi paylaşımı ve ortak eğitim programları',
          'Tedarikçi ve iş ortaklarıyla stratejik işbirlikleri',
          'Sektörel platformlarda aktif katılım ve katkı'
        ],
        stats: { label: 'Ortaklık Süresi', value: '8+ Yıl' }
      }
    },
    {
      icon: 'ri-star-line',
      title: 'Mükemmellik',
      description: 'Her projede en yüksek kalite standartlarını hedeflemek',
      details: {
        subtitle: 'Sıfır Hata Hedefi',
        content: 'Mükemmellik arayışımız hiç bitmez. Six Sigma metodolojisi ve sürekli iyileştirme yaklaşımıyla en yüksek kaliteyi sağlıyoruz.',
        points: [
          'Six Sigma Black Belt sertifikalı uzman kadro',
          'ISO 9001, IATF 16949 standartlarına tam uyum',
          'Sürekli iyileştirme (Kaizen) kültürü',
          'Detaylı kalite metrikleri ve performans takibi'
        ],
        stats: { label: 'Kalite Skoru', value: '99.8%' }
      }
    },
    {
      icon: 'ri-heart-line',
      title: 'Müşteri Odaklılık',
      description: 'Müşteri memnuniyetini her şeyin üstünde tutmak',
      details: {
        subtitle: 'Müşteri Başarısı Bizim Başarımız',
        content: 'Müşterilerimizin ihtiyaçlarını anlamak ve beklentilerini aşmak için çalışıyoruz. Her proje özeldir ve özel ilgi gerektirir.',
        points: [
          '7/24 müşteri destek hattı ve acil müdahale ekibi',
          'Özelleştirilmiş çözümler ve esnek hizmet modelleri',
          'Düzenli müşteri memnuniyet anketleri ve geri bildirim',
          'Müşteri başarı yöneticisi ataması'
        ],
        stats: { label: 'Memnuniyet', value: '%97' }
      }
    },
    {
      icon: 'ri-leaf-line',
      title: 'Sürdürülebilirlik',
      description: 'Çevreye ve topluma duyarlı iş pratikleri uygulamak',
      details: {
        subtitle: 'Yeşil Gelecek İçin',
        content: 'Çevre dostu üretim süreçleri ve sosyal sorumluluk projelerimizle sürdürülebilir bir gelecek inşa ediyoruz.',
        points: [
          'Karbon ayak izi azaltma programları',
          'Geri dönüşüm ve atık yönetimi sistemleri',
          'Yeşil tedarik zinciri yönetimi',
          'Sosyal sorumluluk projeleri ve topluma katkı'
        ],
        stats: { label: 'Karbon Azaltma', value: '%40' }
      }
    }
  ];

  const reasons = [
    {
      icon: 'ri-award-line',
      title: 'Sektörel Uzmanlık',
      description: 'Otomotiv yan sanayinde 15+ yıllık deneyim ve derin bilgi birikimi ile hizmet veriyoruz.',
      stats: '15+ Yıl Deneyim',
      details: {
        subtitle: 'Derin Sektör Bilgisi',
        content: 'Otomotiv yan sanayinde 15 yılı aşkın deneyimimizle, sektörün tüm dinamiklerini ve gereksinimlerini en iyi şekilde biliyoruz.',
        achievements: [
          { label: 'Tamamlanan Proje', value: '500+' },
          { label: 'Sektör Deneyimi', value: '15 Yıl' },
          { label: 'Uzman Kadro', value: '50+' },
          { label: 'Müşteri Portföyü', value: '100+' }
        ],
        expertise: [
          'IATF 16949 kalite yönetim sistemi uzmanlığı',
          'OEM (ana üretici) gereksinimlerine tam uyum',
          'Tier 1, Tier 2 tedarikçi yönetimi deneyimi',
          'Otomotiv özel süreç denetimleri (PPAP, APQP, FMEA)'
        ]
      }
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Kalite Güvencesi',
      description: 'ISO/TS 16949 standartlarına uygun denetim süreçleri ile %99.8 kalite oranı sağlıyoruz.',
      stats: '%99.8 Kalite Oranı',
      details: {
        subtitle: 'Sıfır Hata Garantisi',
        content: 'Katı kalite kontrol süreçlerimiz ve ileri teknoloji ekipmanlarımızla %99.8 kalite oranı sağlıyoruz.',
        achievements: [
          { label: 'Kalite Oranı', value: '%99.8' },
          { label: 'Hata Oranı', value: '20 PPM' },
          { label: 'İlk Geçiş Oranı', value: '%96' },
          { label: 'Müşteri Şikayeti', value: '0.2%' }
        ],
        expertise: [
          'İstatistiksel proses kontrolü (SPC) uygulamaları',
          'Ölçüm sistemi analizi (MSA) ve kalibrasyon',
          'Gelişmiş kalite planlama (APQP) metodolojisi',
          'Hata türü ve etkileri analizi (FMEA)'
        ]
      }
    },
    {
      icon: 'ri-time-line',
      title: 'Hızlı Çözüm',
      description: 'Acil durumlarda 24 saat içinde yerinde destek ve hızlı çözüm üretme kapasitesi.',
      stats: '24 Saat Destek',
      details: {
        subtitle: 'Anında Müdahale',
        content: 'Acil durumlarda 24 saat içinde yerinde olup, üretim durmasını önleyen hızlı çözümler üretiyoruz.',
        achievements: [
          { label: 'Ortalama Müdahale', value: '4 Saat' },
          { label: 'Çözüm Süresi', value: '24 Saat' },
          { label: 'Destek Hattı', value: '7/24' },
          { label: 'Mobil Ekip', value: '10+' }
        ],
        expertise: [
          'Acil durum müdahale protokolleri',
          'Mobil kalite kontrol laboratuvarı',
          'Hızlı kök neden analizi ve düzeltici faaliyet',
          'Geçici çözüm ve kalıcı iyileştirme planları'
        ]
      }
    },
    {
      icon: 'ri-group-line',
      title: 'Uzman Kadro',
      description: 'Alanında uzman, sertifikalı kalite kontrol mühendisleri ve İK profesyonelleri.',
      stats: '50+ Uzman',
      details: {
        subtitle: 'Profesyonel Ekip',
        content: 'Sertifikalı uzmanlardan oluşan güçlü kadromuz, her projede en yüksek kalite standardını garanti eder.',
        achievements: [
          { label: 'Kalite Uzmanı', value: '35+' },
          { label: 'İK Uzmanı', value: '15+' },
          { label: 'Sertifika', value: '120+' },
          { label: 'Eğitim Saati/Yıl', value: '2000+' }
        ],
        expertise: [
          'ASQ (American Society for Quality) sertifikalı uzmanlar',
          'Six Sigma Black Belt ve Green Belt sertifikaları',
          'Lean Manufacturing ve Toyota Production System eğitimleri',
          'Sürekli mesleki gelişim ve uluslararası sertifikasyon'
        ]
      }
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Sürekli Gelişim',
      description: 'Düzenli eğitimler ve güncel teknolojilerle süreçlerinizi sürekli iyileştiriyoruz.',
      stats: '100+ Eğitim/Yıl',
      details: {
        subtitle: 'Kaizen Felsefesi',
        content: 'Sürekli iyileştirme kültürü ile süreçlerinizi optimize ediyor, verimliliği artırıyoruz.',
        achievements: [
          { label: 'Yıllık Eğitim', value: '100+' },
          { label: 'Katılımcı', value: '1500+' },
          { label: 'İyileştirme Projesi', value: '200+' },
          { label: 'Verimlilik Artışı', value: '%35' }
        ],
        expertise: [
          'Kaizen ve sürekli iyileştirme workshopları',
          'Değer akışı haritalama (VSM) çalışmaları',
          '5S ve görsel yönetim uygulamaları',
          'Problem çözme teknikleri (8D, 5 Why, Ishikawa)'
        ]
      }
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Müşteri Memnuniyeti',
      description: 'Müşteri odaklı yaklaşımımız ve 7/24 iletişim desteği ile yanınızdayız.',
      stats: '%98 Memnuniyet',
      details: {
        subtitle: 'Müşteri Odaklı Hizmet',
        content: 'Müşteri memnuniyeti bizim en önemli başarı kriterimizdir. Her geri bildirimi değerlendiriyor ve sürekli gelişiyoruz.',
        achievements: [
          { label: 'Memnuniyet Oranı', value: '%98' },
          { label: 'Tekrar Çalışma', value: '%95' },
          { label: 'NPS Skoru', value: '85' },
          { label: 'Yanıt Süresi', value: '2 Saat' }
        ],
        expertise: [
          'Müşteri ilişkileri yönetimi (CRM) sistemi',
          'Düzenli müşteri ziyaretleri ve performans değerlendirme',
          'Proaktif iletişim ve şeffaf raporlama',
          'Müşteri başarı hikayeleri ve referans programı'
        ]
      }
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <header>
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white shadow-sm'}`}>
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center gap-3">
                <img src="https://static.readdy.ai/image/d71986f07c85c7b2b8264ab25e46d42e/edcc2fca5481151c158722794d786f43.png" alt="AXIOM Logo" className="h-12 w-12 object-contain" />
                <h1 className="text-xl font-bold text-gray-900">AXIOM</h1>
              </Link>
              
              {/* Desktop Menu */}
              <div className="hidden md:flex items-center gap-8">
                <Link to="/" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors">{t('nav_home')}</Link>
                <Link to="/about" className="text-sm font-medium text-teal-600 hover:text-teal-700 transition-colors">{t('nav_about')}</Link>
                <Link to="/services" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors">{t('nav_services')}</Link>
                <Link to="/training" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors">{t('nav_training')}</Link>
                <Link to="/blog" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors">{t('nav_blog')}</Link>
                <Link to="/career" className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors">Kariyer</Link>
                <Link to="/contact" className="bg-teal-600 text-white px-6 py-2 rounded-full text-sm font-medium hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">{t('nav_contact')}</Link>
                <LanguageSwitcher />
              </div>

              {/* Mobile Menu Button */}
              <button 
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden w-10 h-10 flex items-center justify-center text-gray-900 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
              >
                <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
              </button>
            </div>

            {/* Mobile Menu */}
            {isMobileMenuOpen && (
              <div className="md:hidden mt-4 pb-4 border-t border-gray-200 pt-4">
                <div className="flex flex-col gap-2">
                  <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">{t('nav_home')}</Link>
                  <Link to="/about" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-medium text-teal-600 hover:text-teal-700 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">{t('nav_about')}</Link>
                  <Link to="/services" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">{t('nav_services')}</Link>
                  <Link to="/training" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">{t('nav_training')}</Link>
                  <Link to="/blog" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">{t('nav_blog')}</Link>
                  <Link to="/career" onClick={() => setIsMobileMenuOpen(false)} className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors px-3 py-2 rounded-lg hover:bg-teal-50">Kariyer</Link>
                  <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)} className="bg-teal-600 text-white px-6 py-2 rounded-full text-sm font-medium hover:bg-teal-700 transition-colors text-center whitespace-nowrap cursor-pointer">{t('nav_contact')}</Link>
                  <div className="px-3 py-2">
                    <LanguageSwitcher />
                  </div>
                </div>
              </div>
            )}
          </div>
        </nav>
      </header>

      <main>
        {/* Hero Section */}
        <section className="relative pt-32 pb-24 bg-gradient-to-br from-teal-50 via-white to-emerald-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 bg-teal-100 rounded-full px-4 py-2 mb-6">
                <i className="ri-information-line text-teal-600 text-sm"></i>
                <span className="text-teal-700 text-sm font-semibold">{t('about_badge')}</span>
              </div>
              <h1 className="text-6xl md:text-7xl font-bold text-gray-900 mb-6 leading-tight">
                {t('about_title')}
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
                {t('about_description')}
              </p>
            </div>
          </div>
        </section>

        {/* Images Section */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="rounded-3xl overflow-hidden h-96">
                <img 
                  src="https://public.readdy.ai/ai/img_res/9b40d06000db0af3ede984c25c7b062b.jpg" 
                  alt="Danışmanlık Hizmetleri" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="rounded-3xl overflow-hidden h-96">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20minimalist%20professional%20team%20collaboration%20in%20contemporary%20office%20clean%20workspace%20quality%20control%20process%20simple%20background%20corporate%20environment%20natural%20lighting%20business%20excellence&width=800&height=600&seq=about2&orientation=landscape" 
                  alt="Ekip Çalışması" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
              {/* Mission Card */}
              <div 
                onClick={() => setSelectedCard(selectedCard === 'mission' ? null : 'mission')}
                className={`bg-white rounded-3xl p-12 shadow-sm border border-gray-100 cursor-pointer transition-all duration-500 hover:shadow-xl hover:-translate-y-2 ${
                  selectedCard === 'mission' ? 'ring-4 ring-teal-500 shadow-2xl scale-105' : ''
                }`}
              >
                <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center mb-6 transition-transform duration-300 hover:scale-110">
                  <i className="ri-compass-3-line text-3xl text-teal-600"></i>
                </div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6 flex items-center justify-between">
                  Misyonumuz
                  <i className={`ri-arrow-${selectedCard === 'mission' ? 'up' : 'down'}-s-line text-2xl text-teal-600 transition-transform duration-300`}></i>
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  Kalite kontrol ve insan kaynakları alanında en yüksek standartları sunarak, müşterilerimizin operasyonel mükemmelliğe ulaşmasına yardımcı olmak. İnovasyon, güvenilirlik ve sürekli gelişim ilkeleriyle hareket ederek sektörde öncü olmak.
                </p>
                
                <div className={`overflow-hidden transition-all duration-500 ${
                  selectedCard === 'mission' ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
                }`}>
                  <div className="pt-6 border-t border-gray-200 space-y-6">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <i className="ri-target-line text-teal-600"></i>
                        Temel Hedeflerimiz
                      </h3>
                      <ul className="space-y-3">
                        <li className="flex items-start gap-3">
                          <i className="ri-check-line text-teal-600 text-xl mt-1 flex-shrink-0"></i>
                          <span className="text-gray-600">Müşterilerimize sektörün en yüksek kalite standartlarını sunmak ve operasyonel verimliliği artırmak</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <i className="ri-check-line text-teal-600 text-xl mt-1 flex-shrink-0"></i>
                          <span className="text-gray-600">İnovatif çözümler ve teknolojik gelişmelerle süreçleri sürekli iyileştirmek</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <i className="ri-check-line text-teal-600 text-xl mt-1 flex-shrink-0"></i>
                          <span className="text-gray-600">Güvenilir, şeffaf ve uzun vadeli iş ortaklıkları kurmak</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <i className="ri-check-line text-teal-600 text-xl mt-1 flex-shrink-0"></i>
                          <span className="text-gray-600">Çalışanlarımızın gelişimine yatırım yaparak sektörün en iyi ekibini oluşturmak</span>
                        </li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <i className="ri-lightbulb-line text-teal-600"></i>
                        Yaklaşımımız
                      </h3>
                      <div className="grid grid-cols-1 gap-4">
                        <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl p-4">
                          <h4 className="font-semibold text-gray-900 mb-2">Müşteri Odaklılık</h4>
                          <p className="text-sm text-gray-600">Her projeyi müşterinin benzersiz ihtiyaçlarına göre özelleştiriyor, esnek ve çözüm odaklı yaklaşım sunuyoruz.</p>
                        </div>
                        <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl p-4">
                          <h4 className="font-semibold text-gray-900 mb-2">Sürekli İyileştirme</h4>
                          <p className="text-sm text-gray-600">Kaizen felsefesiyle hareket ediyor, süreçlerimizi ve hizmet kalitemizi sürekli geliştiriyoruz.</p>
                        </div>
                        <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl p-4">
                          <h4 className="font-semibold text-gray-900 mb-2">Teknoloji ve İnovasyon</h4>
                          <p className="text-sm text-gray-600">En güncel teknolojileri ve metodolojileri kullanarak sektörde öncü olmaya devam ediyoruz.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Vision Card */}
              <div 
                onClick={() => setSelectedCard(selectedCard === 'vision' ? null : 'vision')}
                className={`bg-white rounded-3xl p-12 shadow-sm border border-gray-100 cursor-pointer transition-all duration-500 hover:shadow-xl hover:-translate-y-2 ${
                  selectedCard === 'vision' ? 'ring-4 ring-teal-500 shadow-2xl scale-105' : ''
                }`}
              >
                <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center mb-6 transition-transform duration-300 hover:scale-110">
                  <i className="ri-eye-line text-3xl text-teal-600"></i>
                </div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6 flex items-center justify-between">
                  Vizyonumuz
                  <i className={`ri-arrow-${selectedCard === 'vision' ? 'up' : 'down'}-s-line text-2xl text-teal-600 transition-transform duration-300`}></i>
                </h2>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  Türkiye'nin en güvenilir kalite kontrol ve insan kaynakları danışmanlık şirketi olmak. Müşterilerimize değer katan, çalışanlarımızın gelişimine önem veren ve sektöre yön veren bir organizasyon olmak.
                </p>
                
                <div className={`overflow-hidden transition-all duration-500 ${
                  selectedCard === 'vision' ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
                }`}>
                  <div className="pt-6 border-t border-gray-200 space-y-6">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <i className="ri-rocket-line text-teal-600"></i>
                        Gelecek Hedeflerimiz
                      </h3>
                      <ul className="space-y-3">
                        <li className="flex items-start gap-3">
                          <i className="ri-check-line text-teal-600 text-xl mt-1 flex-shrink-0"></i>
                          <span className="text-gray-600">2025 yılına kadar Türkiye'nin en büyük 3 kalite kontrol danışmanlık şirketi arasına girmek</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <i className="ri-check-line text-teal-600 text-xl mt-1 flex-shrink-0"></i>
                          <span className="text-gray-600">Uluslararası pazarlara açılarak global bir marka haline gelmek</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <i className="ri-check-line text-teal-600 text-xl mt-1 flex-shrink-0"></i>
                          <span className="text-gray-600">Dijital dönüşüm ve yapay zeka destekli kalite kontrol sistemleri geliştirmek</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <i className="ri-check-line text-teal-600 text-xl mt-1 flex-shrink-0"></i>
                          <span className="text-gray-600">Sektöre 1000+ uzman kazandırmak ve eğitim akademisi kurmak</span>
                        </li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <i className="ri-star-line text-teal-600"></i>
                        Stratejik Önceliklerimiz
                      </h3>
                      <div className="grid grid-cols-1 gap-4">
                        <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl p-4">
                          <h4 className="font-semibold text-gray-900 mb-2">Dijital Dönüşüm</h4>
                          <p className="text-sm text-gray-600">Yapay zeka ve IoT teknolojileriyle kalite kontrol süreçlerini otomatikleştirme ve veri analitiği ile öngörülü bakım sistemleri geliştirme.</p>
                        </div>
                        <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl p-4">
                          <h4 className="font-semibold text-gray-900 mb-2">Sürdürülebilirlik</h4>
                          <p className="text-sm text-gray-600">Çevre dostu üretim süreçleri ve yeşil kalite yönetim sistemleri konusunda öncü olmak.</p>
                        </div>
                        <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl p-4">
                          <h4 className="font-semibold text-gray-900 mb-2">Eğitim ve Gelişim</h4>
                          <p className="text-sm text-gray-600">Sektörün en kapsamlı eğitim programlarını sunarak nitelikli iş gücü yetiştirmeye katkıda bulunmak.</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-r from-teal-600 to-emerald-600 rounded-xl p-6 text-white">
                      <h3 className="text-lg font-semibold mb-2 flex items-center gap-2">
                        <i className="ri-trophy-line text-2xl"></i>
                        2030 Hedefi
                      </h3>
                      <p className="text-sm opacity-90">
                        Avrupa'nın önde gelen kalite kontrol ve insan kaynakları danışmanlık şirketleri arasında yer almak ve 10+ ülkede hizmet vermek.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Info Text */}
            <div className="text-center mt-12">
              <p className="text-gray-500 text-sm flex items-center justify-center gap-2">
                <i className="ri-information-line"></i>
                Daha fazla detay için kartlara tıklayın
              </p>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-gray-900 mb-4">Değerlerimiz</h2>
              <p className="text-lg text-gray-600">İş yapış şeklimizi şekillendiren temel değerlerimiz</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {values.map((value, index) => (
                <div 
                  key={index} 
                  onClick={() => setSelectedValue(selectedValue === index ? null : index)}
                  className={`bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 border border-gray-100 cursor-pointer transition-all duration-500 hover:shadow-xl hover:-translate-y-2 ${
                    selectedValue === index ? 'ring-4 ring-teal-500 shadow-2xl scale-105' : ''
                  }`}
                >
                  <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-4 transition-transform duration-300 hover:scale-110">
                    <i className={`${value.icon} text-2xl text-teal-600`}></i>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed mb-4">{value.description}</p>

                  <div className={`overflow-hidden transition-all duration-500 ${
                    selectedValue === index ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
                  }`}>
                    <div className="pt-4 border-t border-gray-200 space-y-4">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-2">{value.details.subtitle}</h4>
                        <p className="text-sm text-gray-600 mb-4">{value.details.content}</p>
                      </div>

                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                          <i className="ri-list-check text-teal-600"></i>
                          Uygulama Alanları
                        </h4>
                        <ul className="space-y-2">
                          {value.details.points.map((point, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <i className="ri-checkbox-circle-fill text-teal-600 text-sm mt-1 flex-shrink-0"></i>
                              <span className="text-xs text-gray-600">{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="bg-gradient-to-r from-teal-600 to-emerald-600 rounded-lg p-4 text-white">
                        <div className="flex items-center justify-between">
                          <span className="text-xs opacity-90">{value.details.stats.label}</span>
                          <span className="text-2xl font-bold">{value.details.stats.value}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <p className="text-gray-500 text-sm flex items-center justify-center gap-2">
                <i className="ri-information-line"></i>
                Detayları görmek için kartlara tıklayın
              </p>
            </div>
          </div>
        </section>

        {/* Team Section */}
        <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-5xl font-bold text-gray-900 mb-4">Neden AXIOM?</h2>
              <p className="text-lg text-gray-600">Bizi tercih etmeniz için güçlü nedenler</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {reasons.map((item, index) => (
                <div 
                  key={index} 
                  onClick={() => setSelectedReason(selectedReason === index ? null : index)}
                  className={`bg-white rounded-2xl p-8 shadow-sm border border-gray-100 cursor-pointer transition-all duration-500 hover:shadow-xl hover:-translate-y-2 ${
                    selectedReason === index ? 'ring-4 ring-teal-500 shadow-2xl scale-105' : ''
                  }`}
                >
                  <div className="flex items-start gap-6">
                    <div className="w-16 h-16 bg-teal-100 rounded-2xl flex items-center justify-center flex-shrink-0 transition-transform duration-300 hover:scale-110">
                      <i className={`${item.icon} text-3xl text-teal-600`}></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-semibold text-gray-900 mb-3 flex items-center justify-between">
                        {item.title}
                        <i className={`ri-arrow-${selectedReason === index ? 'up' : 'down'}-s-line text-xl text-teal-600 transition-transform duration-300`}></i>
                      </h3>
                      <p className="text-gray-600 leading-relaxed mb-4">{item.description}</p>
                      <div className="inline-flex items-center gap-2 bg-teal-50 rounded-lg px-4 py-2 mb-4">
                        <i className="ri-check-line text-teal-600"></i>
                        <span className="text-sm font-semibold text-teal-700">{item.stats}</span>
                      </div>

                      <div className={`overflow-hidden transition-all duration-500 ${
                        selectedReason === index ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                      }`}>
                        <div className="pt-4 border-t border-gray-200 space-y-6">
                          <div>
                            <h4 className="text-lg font-semibold text-gray-900 mb-3">{item.details.subtitle}</h4>
                            <p className="text-sm text-gray-600 mb-4">{item.details.content}</p>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            {item.details.achievements.map((achievement, idx) => (
                              <div key={idx} className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-xl p-4 text-center">
                                <div className="text-2xl font-bold text-teal-600 mb-1">{achievement.value}</div>
                                <div className="text-xs text-gray-600">{achievement.label}</div>
                              </div>
                            ))}
                          </div>

                          <div>
                            <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                              <i className="ri-star-line text-teal-600"></i>
                              Uzmanlık Alanları
                            </h4>
                            <ul className="space-y-2">
                              {item.details.expertise.map((exp, idx) => (
                                <li key={idx} className="flex items-start gap-2">
                                  <i className="ri-checkbox-circle-fill text-teal-600 text-sm mt-1 flex-shrink-0"></i>
                                  <span className="text-xs text-gray-600">{exp}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <p className="text-gray-500 text-sm flex items-center justify-center gap-2">
                <i className="ri-information-line"></i>
                Detaylı bilgi için kartlara tıklayın
              </p>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
            <div className="lg:col-span-2">
              <Link to="/" className="flex items-center gap-3 mb-4">
                <img src="https://static.readdy.ai/image/d71986f07c85c7b2b8264ab25e46d42e/edcc2fca5481151c158722794d786f43.png" alt="AXIOM Logo" className="h-10 w-10 object-contain" />
                <span className="text-2xl font-bold">AXIOM</span>
              </Link>
              <p className="text-gray-400 text-sm leading-relaxed mb-6">
                {t('footer_description')}
              </p>
              <div className="flex gap-4">
                <a href="https://www.linkedin.com/in/axiom-kalite" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-lg"></i>
                </a>
                <a href="https://x.com/AxiomKalite" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                  <i className="ri-twitter-x-fill text-lg"></i>
                </a>
                <a href="https://www.instagram.com/axiomkalite/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                  <i className="ri-instagram-fill text-lg"></i>
                </a>
                <a href="https://www.facebook.com/profile.php?id=61585433974202" target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-lg"></i>
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold text-lg mb-4">{t('footer_services')}</h4>
              <ul className="space-y-3">
                <li><Link to="/services" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_quality')}</Link></li>
                <li><Link to="/services" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_hr')}</Link></li>
                <li><Link to="/training" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_training')}</Link></li>
                <li><Link to="/services" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_audit')}</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-lg mb-4">{t('footer_corporate')}</h4>
              <ul className="space-y-3">
                <li><Link to="/about" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_about')}</Link></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_team')}</a></li>
                <li><Link to="/career" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_career')}</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_contact')}</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-lg mb-4">{t('footer_resources')}</h4>
              <ul className="space-y-3">
                <li><Link to="/blog" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_blog')}</Link></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_references')}</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_faq')}</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors text-sm cursor-pointer">{t('footer_documents')}</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-gray-400">
              <p>{t('footer_copyright')}</p>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer">
                {t('footer_powered')}
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}